import React, { useEffect, useRef, useCallback, type ReactNode } from 'react'
import Matter from 'matter-js'
import { useWindowDimensions } from '../utils/useWindowDimensions'
import { getRandomInt } from '../utils/number'
import { getRandomTexture } from '../utils/texture'
import { Sunrise } from './Sunrise'

const DROP_DELAY = 40
const MAX_NODE_SIZE = 16

interface PhysicsProps {
  tj: number
  children?: ReactNode
}

interface EngineProps {
  world: Matter.World
}

interface SimulationContext {
  engine: Matter.Engine
  render: Matter.Render
}

export const Physics: React.FC<PhysicsProps> = ({ tj, children }) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const { width, height } = useWindowDimensions()
  const headerHeight = height * 0.08
  const headerSensitiveHeight = height - headerHeight
  const { Composite, Bodies } = Matter
  const timer = (ms: number) => new Promise((res) => setTimeout(res, ms))

  const addWalls = useCallback(
    (engine: Matter.Engine) => {
      const { Bodies, Composite } = Matter

      const wallOpts = {
        isStatic: true,
        render: {
          fillStyle: 'rgba(75, 83, 32, 0)',
        },
      }

      const floor = Bodies.rectangle(width / 2, headerSensitiveHeight, width, 20, wallOpts)
      const leftWall = Bodies.rectangle(
        0,
        headerSensitiveHeight,
        20,
        headerSensitiveHeight * 2,
        wallOpts
      )
      const rightWall = Bodies.rectangle(
        width,
        headerSensitiveHeight,
        20,
        headerSensitiveHeight * 2,
        wallOpts
      )
      const roof = Bodies.rectangle(width / 2, -headerSensitiveHeight * 2, width, 20, wallOpts)

      Composite.add(engine.world, [floor, leftWall, rightWall, roof])
    },
    [width, headerSensitiveHeight]
  )

  const addMouseConstraint = useCallback((engine: Matter.Engine, render: Matter.Render) => {
    const { Mouse, MouseConstraint, Composite } = Matter
    const mouse = Mouse.create(render.canvas)
    const mouseConstraint = MouseConstraint.create(engine, {
      mouse,
      constraint: {
        stiffness: 1,
        render: {
          visible: false,
        },
      },
    })

    Composite.add(engine.world, mouseConstraint)
  }, [])

  const addNodes = useCallback(
    async (engine: EngineProps) => {
      const nodeRadius = Math.min(Math.min(width, height) * 0.035, MAX_NODE_SIZE)
      const spriteScale = nodeRadius / 20

      for (let i = 0; i < tj; i += 1) {
        const offset = getRandomInt(-10, 10)
        const x = width / 2 - offset * 10
        const texture = getRandomTexture()
        const body = Bodies.circle(x, -100, nodeRadius, {
          restitution: 0.3,
          id: i === 0 ? -1 : i,
          render: {
            sprite: {
              texture,
              xScale: spriteScale,
              yScale: spriteScale,
            },
          },
        })
        Matter.Body.setMass(body, 100)
        Composite.add(engine.world, body)
        await timer(DROP_DELAY)
      }
    },
    [width, height, tj, Bodies, Composite]
  )

  const initialize = useCallback(
    (element: HTMLDivElement): SimulationContext => {
      const { Engine, Render } = Matter

      const engine = Engine.create()
      const render = Render.create({
        element,
        engine,
        options: {
          width,
          height: headerSensitiveHeight,
          wireframes: false,
          background: 'transparent',
        },
      })
      addWalls(engine)
      addMouseConstraint(engine, render)

      return { engine, render }
    },
    [addWalls, addMouseConstraint, headerSensitiveHeight, width]
  )

  const run = useCallback(
    (element: HTMLDivElement): SimulationContext => {
      const { Render } = Matter
      const { engine, render } = initialize(element)

      Matter.Runner.run(engine)
      Render.run(render)
      void addNodes(engine)

      return { engine, render }
    },
    [initialize, addNodes]
  )

  useEffect(() => {
    if (!ref.current) return

    const { engine, render } = run(ref.current)

    const { Engine, Render, Composite } = Matter

    return () => {
      // clear the scene
      Render.stop(render)
      Engine.clear(engine)
      Composite.clear(engine.world, false)
      render.canvas.remove()
      render.textures = {}
    }
  }, [run])

  return (
    <div ref={ref} className="physicsContainer">
      <Sunrise tj={tj} width={width} height={height} />
      <div className="physicsOverlay">{children}</div>
    </div>
  )
}
